import type { EChartsOption, ScatterSeriesOption } from 'echarts';

import {
  FONT_SIZE,
  FormatMetric,
  formatAxisValue,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// A scatter point's size, and the range a bubble's sizes run between
// (pixels across)
const POINT_SIZE = 8;
const MIN_BUBBLE_SIZE = 8;
const MAX_BUBBLE_SIZE = 48;

// A bubble's size, by the square root of where its value sits among the
// chart's: its area, not its width, follows its value
export const toBubbleSize = ({
  size,
  min,
  max,
}: {
  size: number;
  min: number;
  max: number;
}) =>
  max === min
    ? (MIN_BUBBLE_SIZE + MAX_BUBBLE_SIZE) / 2
    : MIN_BUBBLE_SIZE +
      (MAX_BUBBLE_SIZE - MIN_BUBBLE_SIZE) *
        Math.sqrt((size - min) / (max - min));

const toValueAxis = ({
  axis,
  theme,
  nameLocation,
}: {
  axis: StudioCharts.MetricAxis;
  theme: StudioCharts.Theme;
  nameLocation: 'middle' | 'end';
}) => ({
  type: 'value' as const,
  // Fitted to the values, as the Highcharts scatter's axes
  scale: true,
  name: axis.name,
  nameLocation,
  nameGap: nameLocation === 'middle' ? 28 : 12,
  nameTextStyle: { color: theme.textMuted, fontSize: FONT_SIZE },
  axisLine: { lineStyle: { color: theme.axisLine } },
  axisTick: { show: false },
  axisLabel: {
    color: theme.textMuted,
    fontSize: FONT_SIZE,
    formatter: formatAxisValue,
    hideOverlap: true,
  },
  splitLine: { lineStyle: { color: theme.gridLine } },
});

// A scatter chart: each dimension member a point, at its x and y metrics'
// values. A bubble chart sizes each by a third metric, and names it.
const toScatterOption = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.ScatterModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption => {
  const { size } = model;
  const sizes = model.points.map((p) => p.size ?? 0);
  const minSize = Math.min(...sizes);
  const maxSize = Math.max(...sizes);
  const series: ScatterSeriesOption = {
    type: 'scatter',
    id: model.seriesId,
    name: model.name,
    color: model.color,
    data: model.points.map((point) => ({
      name: point.name,
      value: [point.x, point.y],
      symbolSize:
        size && point.size !== undefined
          ? toBubbleSize({ size: point.size, min: minSize, max: maxSize })
          : POINT_SIZE,
      ...(model.selectedKeys && !model.selectedKeys.includes(point.key)
        ? { itemStyle: { color: theme.deselected } }
        : {}),
    })),
    // Bubbles see through to those behind them
    itemStyle: { opacity: size ? 0.75 : 1 },
    label: size
      ? {
          show: true,
          formatter: '{b}',
          color: theme.textPrimary,
          fontSize: FONT_SIZE,
        }
      : undefined,
    emphasis: { scale: 1.2 },
  };
  return {
    ...toBaseOption(theme),
    grid: {
      left: 8,
      right: 16,
      top: 28,
      // Room for the x metric's name under the axis
      bottom: 24,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    },
    xAxis: toValueAxis({ axis: model.x, theme, nameLocation: 'middle' }),
    yAxis: toValueAxis({ axis: model.y, theme, nameLocation: 'end' }),
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const point = item
          ? model.points.find((p) => p.name === item.name)
          : undefined;
        if (!point) {
          return '';
        }
        const row = (axis: StudioCharts.MetricAxis, value: number) => ({
          name: axis.name,
          value: formatMetric({ metricId: axis.metricId, value }),
        });
        return toTooltipHtml({
          title: point.name,
          rows: [
            row(model.x, point.x),
            row(model.y, point.y),
            ...(size && point.size !== undefined
              ? [row(size, point.size)]
              : []),
          ],
        });
      },
    },
    series: [series],
  };
};

export default toScatterOption;
